import { reflect } from '@effector/reflect';
import { Checkbox, PasswordInput, Select, TextInput } from '@mantine/core';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { Plus } from 'lucide-react';
import { not } from 'patronum';
import { useTranslation } from 'react-i18next';
import {
	USER_NAME_MAX_LENGTH,
	USER_NAME_MIN_LENGTH,
	USER_NOTE_MAX_LENGTH,
	USER_PASSWORD_MIN_LENGTH,
} from 'entities/user';
import { viewerModel } from 'entities/viewer';
import { UserRole } from 'shared/api';
import { $t, translated } from 'shared/lib';
import { createModalForm } from 'shared/ui';
import { AddUserModel } from '../../model';
import cls from './add-user-modal.module.scss';

export const createAddUserModal = (model: AddUserModel) => {
	const ModalForm = createModalForm(model);

	const NameField = reflect({
		view: TextInput,
		bind: {
			'data-test-id': 'add-user-name-input',
			label: translated('add-user.name'),
			placeholder: translated('add-user.name'),
			errorProps: { 'data-test-id': 'add-user-name-input-error' },
			error: combine(
				[model.form.fields.name.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-user.name'),
						minLength: USER_NAME_MIN_LENGTH,
						maxLength: USER_NAME_MAX_LENGTH,
					}),
			),
			value: model.form.fields.name.$value,
			onChange: (e) => model.form.fields.name.change(e.target.value),
		},
	});

	const PasswordField = reflect({
		view: PasswordInput,
		bind: {
			'data-test-id': 'add-user-password-input',
			label: translated('add-user.password'),
			placeholder: translated('add-user.password'),
			errorProps: { 'data-test-id': 'add-user-password-input-error' },
			error: combine(
				[model.form.fields.password.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-user.password'),
						minLength: USER_PASSWORD_MIN_LENGTH,
					}),
			),
			value: model.form.fields.password.$value,
			onChange: (e) => model.form.fields.password.change(e.target.value),
		},
	});

	const AccountField = reflect({
		view: Select,
		bind: {
			'data-test-id': 'add-user-account-select',
			value: model.form.fields.accountId.$value.map((value) => value?.toString() ?? null),
			onChange: (value) =>
				model.form.fields.accountId.change(value === null ? value : Number(value)),
			data: model.$accounts.map((accounts) =>
				accounts.map(({ id, name }) => ({
					value: id.toString(),
					label: name,
				})),
			),
			disabled: model.form.fields.accountId.$disabled,
			label: translated('add-user.account'),
			placeholder: translated('add-user.account'),
			errorProps: { 'data-test-id': 'add-user-account-select-error' },
			error: combine(
				[model.form.fields.accountId.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-user.account'),
					}),
			),
			searchable: true,
		},
	});

	const RoleField = reflect({
		view: Select,
		bind: {
			'data-test-id': 'add-user-role-select',
			label: translated('add-user.role'),
			placeholder: translated('add-user.role'),
			value: model.form.fields.role.$value.map((value) => value?.toString() ?? null),
			onChange: (value) =>
				model.form.fields.role.change(value === null ? value : (Number(value) as UserRole)),
			data: combine([$t, model.availableRoles]).map(([t, roles]) =>
				roles.map((r) => ({
					value: `${r}`,
					label: t(`user-roles.${r}`),
				})),
			),
			errorProps: { 'data-test-id': 'add-user-role-select-error' },
			error: combine(
				[model.form.fields.role.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-user.role'),
					}),
			),
		},
	});

	const NoteField = reflect({
		view: TextInput,
		bind: {
			'data-test-id': 'add-user-note-input',
			label: translated('add-user.note'),
			placeholder: translated('add-user.note'),
			errorProps: { 'data-test-id': 'add-user-note-input-error' },
			error: combine(
				[model.form.fields.note.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-user.note'),
						maxLength: USER_NOTE_MAX_LENGTH,
					}),
			),
			value: model.form.fields.note.$value,
			onChange: (e) => model.form.fields.note.change(e.target.value),
		},
	});

	const MultiLoginField = reflect({
		view: Checkbox,
		bind: {
			'data-test-id': 'add-user-allow-multi-login-checkbox',
			label: translated('add-user.allow-multi-login'),
			disabled: model.form.fields.hasMultiLogin.$disabled,
			checked: model.form.fields.hasMultiLogin.$value,
			onChange: (e) => model.form.fields.hasMultiLogin.change(e.target.checked),
		},
	});

	const AddUserModalButton = reflect({
		view: ModalForm.Button,
		bind: {
			'data-test-id': 'add-user-button',
			leftSection: <Plus />,
			className: cls.addUserModalButton,
			disabled: not(viewerModel.$canWriteUsers),
			children: translated('add-user.label'),
			modalData: null,
		},
	});

	const AddUserModal = () => {
		const { t } = useTranslation();

		const [pending, closed] = useUnit([model.$pending, model.modalClosed]);

		return (
			<ModalForm
				title={t('add-user.title')}
				cancelButtonProps={{
					children: t('add-user.cancel'),
				}}
				submitButtonProps={{
					children: t('add-user.add'),
					loading: pending,
				}}
				onExitTransitionEnd={closed}
			>
				<NameField />
				<PasswordField />
				<AccountField />
				<RoleField />
				<NoteField />
				<MultiLoginField />
			</ModalForm>
		);
	};

	return Object.assign(AddUserModal, {
		Button: AddUserModalButton,
	});
};
